import { kstDateOf, kstDateRangeDays } from '@shared/utils/kstTime.util';

import {
  OrderStatBrandRow,
  OrderStatDayRow,
  OrderStatDrillDown,
  OrderStatFilters,
  OrderStatKind,
  OrderStatOrder,
  OrderStatResult,
} from './adminOrderStat.types';
import { BrandNameResolver, defaultBrandNameResolver } from './analyticsBrandName';

//* ── 지표 정의 — 백엔드 대시보드(coffin-out-daily)와 같은 규칙을 그대로 옮긴다 ──
//*
//* 화환(shopOrder): status ∈ {Paid, Confirmed}, type ≠ ReturnGift, docStatus ≠ Deleted.
//*   금액 = paymentDetail.paymentByCash (wreathOrderMatch와 동일).
//*   취소는 status 필터에서 이미 빠지므로 따로 차감하지 않는다.
//*
//* 답례품(giftOrder): docStatus = Active,
//*   status ∈ {Paid, Confirmed, CancelRequested, Canceled} — 취소 건도 끌어와야
//*   취소액을 뺄 수 있어서 포함한다(SUMMARY_PAYMENT_STATUSES).
//*   순매출 = max(0, paymentByCash − 취소액), 전체취소면 취소액 = 결제액 전액.
//*   순수량 = Σ(라인 base − 취소수량), 쿠폰형 base는 couponDetail.totalCount.
//*   ⚠️ refundQuantity(발송 후 환불)는 매출·수량에서 차감하지 않는다 — 대시보드 관례.
//*
//* 원가·수익(priceWhole, priceRebate*)은 여기서 다루지 않는다.
//* 도매가가 표기값/실도매 두 갈래라 정산 화면과 정의가 갈리기 때문 (단가표·정산 페이지 소관).

const CANCELED = 'Canceled';

/** 그룹 키 — 멀티 브랜드 상조는 fevent의 bugoBrandAgencyIndex까지 묶어 나눈다 */
export const orderBrandKeyOf = (order: OrderStatOrder): string => {
  const brand = order.bugoBrand;
  const brandId = !brand ? '' : typeof brand === 'string' ? brand : brand._id;
  const fevent = order.fevent;
  const index =
    fevent && typeof fevent !== 'string' && typeof fevent.bugoBrandAgencyIndex === 'number'
      ? fevent.bugoBrandAgencyIndex
      : '';
  return `${brandId}#${index}`;
};

//* 이름은 해석기를 통해 얻는다 — 문서마다 bugoBrand를 populate하지 않기 위해서
//* (`useAnalyticsBrandNames`). 해석기를 안 주면 문서에 붙어온 값을 그대로 쓴다.
export const orderBrandNameOf = (
  order: OrderStatOrder,
  resolve: BrandNameResolver = defaultBrandNameResolver,
): string => {
  const fevent = order.fevent;
  const index =
    fevent && typeof fevent !== 'string' ? fevent.bugoBrandAgencyIndex : undefined;
  return resolve(order.bugoBrand, index);
};

const numberOf = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

/** 주문일(KST 'YYYY-MM-DD'). 화환은 판매일, 답례품은 생성일 축 */
export const orderDayKey = (
  order: OrderStatOrder,
  kind: OrderStatKind,
): string | null => {
  const raw =
    kind === 'flower' ? order.paymentDetail?.purchasedAt ?? order.createdAt : order.createdAt;
  if (!raw) return null;
  const date = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  return kstDateOf(date);
};

export const isCanceledOrder = (order: OrderStatOrder): boolean =>
  order.paymentDetail?.status === CANCELED;

/** 순매출 — 화환은 결제액 그대로, 답례품은 취소액을 뺀다 */
export const netAmountOf = (order: OrderStatOrder, kind: OrderStatKind): number => {
  const cash = numberOf(order.paymentDetail?.paymentByCash);
  if (kind === 'flower') return cash;
  //* 전체취소는 결제 전액이 취소액 (백엔드 GIFT_NET_STAGES와 동일)
  const canceled = isCanceledOrder(order)
    ? cash
    : numberOf(order.paymentDetail?.cancelledTotalPrice);
  return Math.max(0, cash - canceled);
};

/** 순수량 — 답례품은 라인 base에서 취소수량을 뺀다 (환불수량은 빼지 않는다) */
export const netQuantityOf = (order: OrderStatOrder, kind: OrderStatKind): number => {
  if (kind === 'flower') return numberOf(order.orderDetail?.quantity) || 1;
  const canceledAll = isCanceledOrder(order);
  return (order.orderDetails ?? []).reduce((sum, line) => {
    //* 쿠폰형의 진짜 수량은 couponDetail.totalCount (quantity와 다를 수 있다)
    const base =
      line.type === 'Coupon'
        ? numberOf(line.couponDetail?.totalCount) || numberOf(line.quantity)
        : numberOf(line.quantity);
    const canceled = canceledAll
      ? base
      : Math.min(base, numberOf(line.cancelQuantity));
    return sum + Math.max(0, base - canceled);
  }, 0);
};

export const matchesOrderFilters = (
  order: OrderStatOrder,
  filters: OrderStatFilters,
): boolean =>
  filters.brandKeys.length === 0 || filters.brandKeys.includes(orderBrandKeyOf(order));

const emptyRow = (key: string, brandName: string): OrderStatBrandRow => ({
  key,
  brandName,
  orderCount: 0,
  amount: 0,
  feventCount: 0,
  quantity: 0,
  canceledCount: 0,
});

const feventIdOf = (order: OrderStatOrder): string | null => {
  const fevent = order.fevent;
  if (!fevent) return null;
  return typeof fevent === 'string' ? fevent : fevent._id;
};

/** 합계 행에서 그룹 식별용 필드(key/brandName)를 떼어낸다 */
const totalsOf = (row: OrderStatBrandRow): OrderStatResult['totals'] => {
  const { key, brandName, ...rest } = row;
  void key;
  void brandName;
  return rest;
};

/**
 * 주문 목록 → 상조별·일별 집계.
 * `days`는 조회 기간 전체를 빈 날 포함해 채운다(추이·달력이 끊기지 않도록).
 */
export const aggregateOrderStat = (
  orders: OrderStatOrder[],
  kind: OrderStatKind,
  filters: OrderStatFilters,
  period: { from: string; to: string },
  resolveBrandName?: BrandNameResolver,
): OrderStatResult => {
  const brandMap = new Map<string, OrderStatBrandRow>();
  const dayMap = new Map<string, OrderStatDayRow>();
  const totals = emptyRow('', '');
  //* 상가 수는 중복 제거가 필요해 그룹별로 따로 모은다
  const brandFevents = new Map<string, Set<string>>();
  const totalFevents = new Set<string>();

  orders.forEach((order) => {
    if (!matchesOrderFilters(order, filters)) return;

    const canceled = kind === 'gift' && isCanceledOrder(order);
    const amount = netAmountOf(order, kind);
    const quantity = netQuantityOf(order, kind);
    const key = orderBrandKeyOf(order);

    let row = brandMap.get(key);
    if (!row) {
      row = emptyRow(key, orderBrandNameOf(order, resolveBrandName));
      brandMap.set(key, row);
      brandFevents.set(key, new Set());
    }

    //* 전체취소 건은 "주문 건수"에서 빼되 취소 건수로 따로 센다
    if (!canceled) {
      row.orderCount += 1;
      totals.orderCount += 1;
    } else {
      row.canceledCount += 1;
      totals.canceledCount += 1;
    }
    row.amount += amount;
    row.quantity += quantity;
    totals.amount += amount;
    totals.quantity += quantity;

    const feventId = feventIdOf(order);
    if (feventId) {
      brandFevents.get(key)?.add(feventId);
      totalFevents.add(feventId);
    }

    const dayKey = orderDayKey(order, kind);
    if (dayKey) {
      let day = dayMap.get(dayKey);
      if (!day) {
        day = { date: dayKey, orderCount: 0, amount: 0 };
        dayMap.set(dayKey, day);
      }
      if (!canceled) day.orderCount += 1;
      day.amount += amount;
    }
  });

  brandMap.forEach((row, key) => {
    row.feventCount = brandFevents.get(key)?.size ?? 0;
  });
  totals.feventCount = totalFevents.size;

  const brands = Array.from(brandMap.values()).sort(
    (a, b) => b.amount - a.amount || a.brandName.localeCompare(b.brandName),
  );

  const days: OrderStatDayRow[] = kstDateRangeDays(period.from, period.to).map(
    (date) => dayMap.get(date) ?? { date, orderCount: 0, amount: 0 },
  );

  return { totals: totalsOf(totals), brands, days };
};

/** 현재 필터 + 드릴다운 조건에 맞는 주문 목록 */
export const drillDownOrders = (
  orders: OrderStatOrder[],
  kind: OrderStatKind,
  filters: OrderStatFilters,
  drill: OrderStatDrillDown,
): OrderStatOrder[] =>
  orders
    .filter((order) => {
      if (!matchesOrderFilters(order, filters)) return false;
      if (drill.brandKey !== undefined && orderBrandKeyOf(order) !== drill.brandKey) {
        return false;
      }
      if (drill.date !== undefined && orderDayKey(order, kind) !== drill.date) {
        return false;
      }
      if (drill.dateFrom !== undefined || drill.dateTo !== undefined) {
        const day = orderDayKey(order, kind);
        //* 기준일이 없는 주문은 구간에 넣을 근거가 없다
        if (!day) return false;
        if (drill.dateFrom !== undefined && day < drill.dateFrom) return false;
        if (drill.dateTo !== undefined && day > drill.dateTo) return false;
      }
      return true;
    })
    //* 최근 주문부터
    .sort((a, b) =>
      (orderDayKey(b, kind) ?? '').localeCompare(orderDayKey(a, kind) ?? ''),
    );

/** 목록에 쓸 상품명 */
export const orderItemNameOf = (order: OrderStatOrder, kind: OrderStatKind): string => {
  if (kind === 'flower') return order.orderDetail?.shopItemEmbed?.name ?? '';
  const lines = order.orderDetails ?? [];
  const first = lines[0]?.giftItemEmbed?.name ?? '';
  return lines.length > 1 ? `${first} 외 ${lines.length - 1}건` : first;
};

/** 목록에 쓸 고인명 */
export const orderDeceasedNameOf = (order: OrderStatOrder): string => {
  const fevent = order.fevent;
  if (!fevent || typeof fevent === 'string') return '';
  return fevent.deceasedInfo?.name ?? '';
};
